import React, { useEffect, useLayoutEffect, useState } from 'react';
import useAutoDockingController, { AutoDockingController } from './useAutoDockingController';
import AutoDockingStatusIndicator from '../../indicators/autoDockingStatus';
import useDockingKeyPressStateChart from './useDockingKeyPressStateChart';
import { setParameter } from 'actions/setParam';
import { SET_CONTROLLER_STATUS } from 'actions/types';
import { connect, ConnectedProps } from 'react-redux';
import { AppRootState } from 'reducers';

type AutoDockingInputProps = {
	navDataChannel?: RTCDataChannel;
	isPeerConnectionPaused: boolean;
	isVideoVisible: boolean;
	onActivenessChanged: (isActive: boolean) => void;
};

type AutoDockingStatus = AutoDockingController['status'];
const reduxConnector = connect((state: AppRootState) => ({}), { setParameter });

type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
type ComponentProps = PropsFromRedux & AutoDockingInputProps;
/**
 * Handles capturing keyboard events related to docking,
 * and forwarding to AutoDockingController
 * */
function AutoDockingInput({
	navDataChannel,
	isPeerConnectionPaused,
	isVideoVisible,
	onActivenessChanged,
	setParameter,
}: ComponentProps) {
	const controller = useAutoDockingController({
		datachannel: navDataChannel,
		isPeerConnectionPaused,
		isVideoVisible,
	});
	const [controllerStatus, setControllerStatus] = useState<AutoDockingStatus>(controller.status);

	const {
		state: keyPressState,
		onKeyDown,
		onKeyUp,
		onDockingInitiated,
		onDockingTerminated,
		onDockingError,
	} = useDockingKeyPressStateChart({
		sendSTART: controller.start,
		sendSTOP: controller.stop,
		sendCONTINUE: controller.onDockCONTINUE,
		sendRESET: controller.reset,
	});

	useEffect(() => {
		const isKeyPressedDown = (
			[
				'pressedButWaiting',
				'pressedAndEngaged.withKeyDown',
				'pressedAndEngaged.withKeyUp',
			] as Array<typeof keyPressState>
		).includes(keyPressState);
		onActivenessChanged(isKeyPressedDown);
	}, [keyPressState]);

	useEffect(() => {
		setParameter('controllerStatus', SET_CONTROLLER_STATUS, controllerStatus);
	}, [controllerStatus, setParameter]);

	useLayoutEffect(() => {
		const onStatusChanged = () => {
			setControllerStatus(controller.status);

			const { stage, state } = controller.status;
			if (state === 'FAILED') {
				onDockingError();
			} else if (stage === 'STARTING' && state === 'SUCCESS') {
				onDockingInitiated();
			} else if (stage === 'DOCKING' && state === 'SUCCESS') {
				onDockingTerminated();
			}
		};
		controller.addEventListener('status-changed', onStatusChanged);
		return () => {
			controller.removeEventListener('status-changed', onStatusChanged);
		};
	}, [controller, onDockingInitiated, onDockingTerminated, onDockingError, setParameter]);

	useLayoutEffect(() => {
		document.addEventListener('keydown', onKeyDown, { capture: true });
		document.addEventListener('keyup', onKeyUp, { capture: true });
		return () => {
			document.removeEventListener('keydown', onKeyDown, { capture: true });
			document.removeEventListener('keyup', onKeyUp, { capture: true });
		};
	}, [onKeyDown, onKeyUp]);

	return <AutoDockingStatusIndicator status={controllerStatus} />;
}

export default reduxConnector(AutoDockingInput);
